// Fleet Management — unified page
// Go agenti (DB) + Alloy agenti (config) + SPLA + Logy + RDS + SQL + KMS
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../../lib/i18n';
import { api } from '../../lib/api';
import { mcpCall } from '../../lib/mcp';
import { DEFAULT_API_PORT, DEFAULT_AGENT_PORT } from '../../lib/node-ports';
import { toast } from 'sonner';
import { GroupsTab } from './fleet-groups-tab';
import { CatalogTab } from './fleet-catalog-tab';
import { Boxes } from 'lucide-react';
import {
  AsyncStatusToggle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  InlineActions,
  Modal,
  PageHeader,
  RefreshButton,
  SearchInput,
  SectionHeader,
  Select,
  StatCard,
  StatsGrid,
  StatusDot,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Toggle,
} from '../../components/ui';
import {
  Activity,
  AlertCircle,
  BarChart2,
  CheckCircle2,
  Copy,
  Database,
  Download,
  FileText,
  HardDrive,
  Layers,
  LayoutDashboard,
  List,
  Lock,
  Monitor,
  Plus,
  RefreshCw,
  Server,
  Settings,
  Shield,
  Terminal,
  Trash2,
  X,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface FleetAgent {
  id: number;
  agent_id: string;
  hostname: string;
  os: string;
  os_version?: string;
  agent_type: 'basic' | 'full' | 'alloy';
  status: 'online' | 'offline';
  last_seen?: string;
  last_heartbeat?: string;
  customer_id?: string;
  group_name?: string;
  agent_version?: string;
  pending_commands?: number;
}

interface FleetToken {
  hash: string;
  id?: string;
  label: string;
  agent_type: string;
  customer_id?: string;
  group_name?: string;
  expires_at: string;
  used: boolean;
  revoked: boolean;
  revoked_at?: string;
  created_at: string;
  install_sh?: string;
  install_ps1?: string;
  uninstall_sh?: string;
  use_count?: number;
  max_uses?: number;
}

interface AlloyAgent {
  id: string;
  hostname?: string;
  enabled?: boolean;
  status?: string;
  last_heartbeat?: string;
  customer_id?: string;
  sources?: string[];
  roles?: string[];
  customer?: { id: string; label: string; color?: string };
}

interface Customer {
  id: string;
  label: string;
  color?: string;
}

interface SplaComputer {
  computer: string;
  unique_users: number;
  event_count: number;
}
interface SplaMonth {
  month: string;
}
interface RdsUser {
  user: string;
  first_login: string;
  login_count: number;
  last_login: string;
}
interface RdsAgent {
  id: string;
  month: string;
  unique_count: number;
  event_count: number;
  last_processed_ts?: string;
  users?: RdsUser[];
}
interface SqlInstance {
  instance: string;
  edition?: string;
  version?: string;
  version_name?: string;
  license_model?: 'core' | 'core_or_cal' | 'free' | 'unknown';
  license_type: 'core' | 'user_cal' | 'free';
  cpu_count?: number;
  socket_count?: number;
  cores_per_socket?: number;
  numa_node_count?: number;
  physical_memory_mb?: number;
  unique_user_count: number;
  last_seen?: string;
}
interface SqlAgent {
  agent: string;
  month: string | null;
  updated_at?: string | null;
  instances: SqlInstance[];
}
interface KmsClient {
  cmid: string;
  machine_name: string;
  ip: string;
  product: string;
  license_status: number;
  first_seen: string;
  last_contact: string;
  contact_count: number;
  tag?: 'pre_production' | 'test' | null;
}
interface KmsAgent {
  agent: string;
  month: string;
  unique_count: number;
  billable_count: number;
  event_count: number;
  last_processed_ts?: string | null;
  clients?: KmsClient[];
}
interface LogEntry {
  time: string;
  agent: string;
  channel: string;
  customer: string;
  message: string;
}
interface MetricNode {
  host: string;
  measurement: string;
  tags: Record<string, unknown>;
  fields: Record<string, unknown>;
  ts: string;
}
interface FleetLogsResponse {
  entries: LogEntry[];
  total: number;
  metrics: {
    available: boolean;
    nodes: MetricNode[];
    reason?: string;
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' });
}
function copyText(t: string) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(t).then(() => toast.success(i18n.t('fleet.copiedToast'))).catch(
      () => copyFallback(t),
    );
  } else {
    copyFallback(t);
  }
}
function copyFallback(t: string) {
  const el = document.createElement('textarea');
  el.value = t;
  el.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
    toast.success(i18n.t('fleet.copiedToast'));
  } catch {
    toast.error(i18n.t('fleet.copyFailedToast'));
  }
  document.body.removeChild(el);
}

function metricForAgent(nodes: MetricNode[], agent: string): MetricNode | undefined {
  const normalized = agent.trim().toLowerCase();
  const short = normalized.split('.')[0];
  return nodes.find((node) => {
    const host = node.host.trim().toLowerCase();
    return host === normalized || host.split('.')[0] === short;
  });
}
function curMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

function DashboardTab() {
  const { t } = useTranslation();
  const { data: fleetAgents = [] } = useQuery({
    queryKey: ['fl-agents'],
    queryFn: () => mcpCall<FleetAgent[]>('fleet.agent_list'),
  });
  const { data: alloyAgents = [] } = useQuery({
    queryKey: ['fl-alloy'],
    queryFn: () => mcpCall<AlloyAgent[]>('fleet.alloy_list'),
  });

  const online = (fleetAgents as FleetAgent[]).filter((a) => a.status === 'online').length;
  const alloyActive = (alloyAgents as AlloyAgent[]).filter((a) => a.enabled !== false).length;

  const { data: settings } = useQuery({
    queryKey: ['fl-settings'],
    queryFn: () => mcpCall<Record<string, string>>('fleet.settings_get'),
  });
  const qc = useQueryClient();
  const rustdeskOn = settings?.rustdesk === 'true';
  const rustdeskSave = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!enabled) {
        await mcpCall('fleet.settings_set', { rustdesk: 'false' });
        return 0;
      }
      const started = await mcpCall<{ ok?: boolean; error?: string }>('rustdesk.server_start', {});
      if (started?.ok === false) throw new Error(started.error || t('rustdesk.serverStartFailed', 'Server se nespustil'));
      const deployed = await mcpCall<{ ok?: boolean; queued?: string[]; error?: string }>('rustdesk.fleet_deploy', {});
      if (deployed?.ok === false) throw new Error(deployed.error || t('fleet.rustdeskNone', 'Žádná stanice ve fleetu'));
      await mcpCall('fleet.settings_set', { rustdesk: 'true' });
      return deployed.queued?.length ?? 0;
    },
    onSuccess: (queued) => {
      void qc.invalidateQueries({ queryKey: ['fl-settings'] });
      if (queued > 0) toast.success(t('fleet.rustdeskQueued', { count: queued }));
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className='space-y-6'>
      <Card>
        <CardContent className='flex items-center justify-between gap-4 py-4'>
          <div className='min-w-0'>
            <p className='text-sm font-medium text-white'>RustDesk</p>
            <p className='text-xs text-gray-500'>{t('fleet.rustdeskHint', 'Nainstaluje se na stanice ve fleetu. Připojení je pak u stanice.')}</p>
          </div>
          <Toggle
            value={rustdeskOn}
            saving={rustdeskSave.isPending}
            onSave={async (enabled) => {
              await rustdeskSave.mutateAsync(enabled);
            }}
          />
        </CardContent>
      </Card>
      <StatsGrid columns={4}>
        <StatCard
          icon={Server}
          label={t('fleet.statFleetAgents')}
          value={(fleetAgents as FleetAgent[]).length}
          sub={t('fleet.subGoAgentsDb')}
          variant='info'
        />
        <StatCard
          icon={CheckCircle2}
          label={t('fleet.statOnline')}
          value={online}
          sub={t('fleet.subActive')}
          variant='success'
        />
        <StatCard
          icon={Monitor}
          label={t('fleet.statAlloyAgents')}
          value={(alloyAgents as AlloyAgent[]).length}
          sub={t('fleet.subConfigBased')}
          variant='info'
        />
        <StatCard
          icon={Activity}
          label={t('fleet.statAlloyActive')}
          value={alloyActive}
          sub={t('fleet.subEnabled')}
          variant='success'
        />
      </StatsGrid>

      {(fleetAgents as FleetAgent[]).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle icon={Server}>{t('fleet.lastFleetAgentsTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-2'>
              {(fleetAgents as FleetAgent[]).slice(0, 5).map((a) => (
                <div
                  key={a.agent_id}
                  className='flex items-center gap-3 p-3 rounded-xl bg-gray-900/40 border border-gray-800'
                >
                  <StatusDot status={a.status} />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-white truncate'>{a.hostname}</p>
                    <p className='text-xs text-gray-500'>{a.os} · {a.agent_type}</p>
                  </div>
                  <span className='text-xs text-gray-500'>{fmtDate(a.last_seen)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(alloyAgents as AlloyAgent[]).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle icon={Monitor}>{t('fleet.lastAlloyAgentsTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-2'>
              {(alloyAgents as AlloyAgent[]).slice(0, 5).map((a) => (
                <div
                  key={a.id}
                  className='flex items-center gap-3 p-3 rounded-xl bg-gray-900/40 border border-gray-800'
                >
                  <StatusDot status={a.status ?? (a.enabled !== false ? 'online' : 'offline')} />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-white font-mono truncate'>{a.id}</p>
                    <p className='text-xs text-gray-500'>{a.hostname || '—'}</p>
                  </div>
                  <span className='text-xs text-gray-500'>
                    {a.last_heartbeat ? fmtDate(a.last_heartbeat) : '—'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Go Agents tab ─────────────────────────────────────────────────────────────

function GoAgentsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: agents = [], isLoading, refetch } = useQuery({
    queryKey: ['fl-agents'],
    queryFn: () => mcpCall<FleetAgent[]>('fleet.agent_list'),
  });

  const del = useMutation({
    mutationFn: (agent_id: string) => mcpCall('fleet.agent_remove', { agent_id }),
    onSuccess: () => {
      toast.success(t('fleet.agentMarkedForUninstallToast'));
      qc.invalidateQueries({ queryKey: ['fl-agents'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <CardTitle icon={Server}>{t('fleet.goFleetAgentsTitle')}</CardTitle>
          <div className='flex items-center gap-2'>
            <span className='text-sm text-gray-500'>
              {t('fleet.totalCountLabel', { count: (agents as FleetAgent[]).length })}
            </span>
            <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading
          ? <p className='text-sm text-gray-500 py-4'>{t('fleet.loadingLabel')}</p>
          : (agents as FleetAgent[]).length === 0
          ? (
            <EmptyState
              icon={<Server className='w-12 h-12 text-gray-700' />}
              title={t('fleet.noAgentsTitle')}
              description={t('fleet.noFleetAgentsDescription')}
            />
          )
          : (
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-800'>
                  <th className='text-left py-3 px-4 text-xs text-gray-400'>
                    {t('fleet.colStatus')}
                  </th>
                  <th className='text-left py-3 px-4 text-xs text-gray-400'>
                    {t('fleet.colHostname')}
                  </th>
                  <th className='text-left py-3 px-4 text-xs text-gray-400'>{t('fleet.colOs')}</th>
                  <th className='text-left py-3 px-4 text-xs text-gray-400'>
                    {t('fleet.colType')}
                  </th>
                  <th className='text-left py-3 px-4 text-xs text-gray-400'>
                    {t('fleet.colCommands')}
                  </th>
                  <th className='text-left py-3 px-4 text-xs text-gray-400'>
                    {t('fleet.colLastSeen')}
                  </th>
                  <th className='py-3 px-4 w-16' />
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800'>
                {(agents as FleetAgent[]).map((a) => (
                  <tr key={a.agent_id} className='group'>
                    <td className='py-3 px-4'>
                      <StatusDot status={a.status} withLabel />
                    </td>
                    <td className='py-3 px-4'>
                      <p className='text-white'>{a.hostname}</p>
                      <p className='text-xs text-gray-500 font-mono'>{a.agent_id}</p>
                    </td>
                    <td className='py-3 px-4 text-gray-400 text-xs'>{a.os}</td>
                    <td className='py-3 px-4'>
                      <Badge
                        variant={a.agent_type === 'full'
                          ? 'purple'
                          : a.agent_type === 'alloy'
                          ? 'blue'
                          : 'gray'}
                      >
                        {a.agent_type}
                      </Badge>
                    </td>
                    <td className='py-3 px-4'>
                      {(a.pending_commands ?? 0) > 0 && (
                        <Badge variant='yellow' dot>
                          {t('fleet.pendingCommandsBadge', { count: a.pending_commands })}
                        </Badge>
                      )}
                    </td>
                    <td className='py-3 px-4 text-xs text-gray-400'>{fmtDate(a.last_seen)}</td>
                    <td className='py-3 px-4'>
                      <InlineActions.Row
                        onDelete={() => del.mutate(a.agent_id)}
                        deleteConfirm
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </CardContent>
    </Card>
  );
}

// ── Alloy Agents tab ──────────────────────────────────────────────────────────

interface Template {
  id: string;
  label: string;
  description?: string;
  required?: boolean;
  enabled?: boolean;
  sources: string[];
}

interface AgentTask {
  id: string;
  name: string;
  hash: string;
  interval_min: number;
}

function AlloyEditModal({ agent, customers, onClose }: {
  agent: AlloyAgent;
  customers: Customer[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [hostname, setHostname] = useState(agent.hostname ?? '');
  const [customerId, setCustomerId] = useState(agent.customer_id ?? '');
  const [enabled, setEnabled] = useState(agent.enabled !== false);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [tplLoaded, setTplLoaded] = useState(false);
  const [reinstallScripts, setReinstallScripts] = useState<
    { cmd_ps1?: string; cmd_sh?: string; expires_at?: string } | null
  >(null);

  const { data: templates = [] } = useQuery({
    queryKey: ['fl-templates'],
    queryFn: () => mcpCall<Template[]>('fleet.template_list'),
  });

  const { data: agentTpl } = useQuery({
    queryKey: ['fl-agent-templates', agent.id],
    queryFn: () =>
      mcpCall<{ templates: string[]; tasks: AgentTask[] }>('fleet.alloy_templates_get', {
        agent_id: agent.id,
      }),
  });

  // init selectedTemplates po načtení
  if (agentTpl && !tplLoaded) {
    setSelectedTemplates(agentTpl.templates);
    setTplLoaded(true);
  }

  const saveMeta = useMutation({
    mutationFn: () =>
      mcpCall('fleet.alloy_update', {
        agent_id: agent.id,
        hostname,
        customer_id: customerId || undefined,
        enabled,
      }),
    onError: (e: Error) => toast.error(e.message),
  });

  const saveTpls = useMutation({
    mutationFn: () =>
      mcpCall('fleet.alloy_templates_set', {
        agent_id: agent.id,
        template_ids: selectedTemplates,
      }),
    onError: (e: Error) => toast.error(e.message),
  });

  const sendUpdateConfig = useMutation({
    mutationFn: () =>
      mcpCall('fleet.agent_command', { agent_id: agent.id, command: 'update_config' }),
    onError: (e: Error) => toast.error(t('fleet.deployFailedError', { message: e.message })),
  });

  const genReinstall = useMutation({
    mutationFn: () =>
      mcpCall<{ cmd_ps1: string; cmd_sh: string; expires_at: string }>('fleet.reinstall_token', {
        agent_id: agent.id,
      }),
    onSuccess: (r) => setReinstallScripts(r),
    onError: (e: Error) => toast.error(e.message),
  });

  const handleSave = async () => {
    await Promise.all([saveMeta.mutateAsync(), saveTpls.mutateAsync()]);
    toast.success(t('fleet.savedToast'));
    qc.invalidateQueries({ queryKey: ['fl-alloy'] });
    onClose();
  };

  const handleSaveAndDeploy = async () => {
    await Promise.all([saveMeta.mutateAsync(), saveTpls.mutateAsync()]);
    await sendUpdateConfig.mutateAsync();
    toast.success(t('fleet.savedAndConfigSentToast'));
    qc.invalidateQueries({ queryKey: ['fl-alloy'] });
    onClose();
  };

  const toggleTemplate = (id: string) => {
    const tpl = (templates as Template[]).find((item) => item.id === id);
    if (tpl?.required) return;
    setSelectedTemplates((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const tasks = agentTpl?.tasks ?? [];
  const saving = saveMeta.isPending || saveTpls.isPending || sendUpdateConfig.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <>
          {t('fleet.settingsModalTitle')}{' '}
          <span className='font-mono text-blue-400'>{agent.id}</span>
        </>
      }
      footer={
        <div className='flex gap-3'>
          <Button variant='secondary' onClick={onClose} disabled={saving} className='flex-1'>
            {t('fleet.cancelButton')}
          </Button>
          <Button variant='ghost' onClick={handleSave} loading={saving} disabled={saving}>
            {t('fleet.saveButton')}
          </Button>
          <Button
            variant='primary'
            onClick={handleSaveAndDeploy}
            loading={saving}
            disabled={saving}
            icon={<RefreshCw size={13} />}
            className='flex-1'
          >
            {t('fleet.saveAndDeployButton')}
          </Button>
        </div>
      }
    >
      <div className='space-y-5'>
        <div className='grid grid-cols-2 gap-3'>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>Hostname</label>
            <input
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500'
            />
          </div>
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>{t('fleet.customerLabel')}</label>
            <Select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className='w-full'
            >
              <option value=''>{t('fleet.noCustomerOption')}</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </Select>
          </div>
        </div>

        <Toggle
          value={enabled}
          onSave={(v) => setEnabled(v)}
          label={t('fleet.enabledToggleLabel')}
        />

        {/* Šablony */}
        <div>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2'>
            {t('fleet.loggingTemplatesLabel')}
          </p>
          <div className='space-y-1.5'>
            {(templates as Template[]).filter((tpl) => tpl.enabled !== false).map((tpl) => {
              const active = selectedTemplates.includes(tpl.id);
              return (
                <label
                  key={tpl.id}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    active
                      ? 'border-blue-500/40 bg-blue-600/5'
                      : 'border-gray-800 hover:border-gray-700'
                  } ${tpl.required ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <input
                    type='checkbox'
                    checked={active}
                    onChange={() => toggleTemplate(tpl.id)}
                    disabled={tpl.required}
                    className='mt-0.5 accent-blue-500'
                  />
                  <div className='min-w-0 flex-1'>
                    <p className='text-sm font-medium text-white'>
                      {tpl.label}
                      {tpl.required && (
                        <span className='ml-1.5 text-[10px] text-gray-500'>
                          {t('fleet.requiredSuffix')}
                        </span>
                      )}
                    </p>
                    {tpl.description && (
                      <p className='text-xs text-gray-500 mt-0.5'>{tpl.description}</p>
                    )}
                    <div className='flex flex-wrap gap-1 mt-1.5'>
                      {tpl.sources.map((s) => (
                        <span
                          key={s}
                          className='text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 font-mono'
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Naplánované úlohy */}
        {tasks.length > 0 && (
          <div>
            <p className='text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2'>
              {t('fleet.scheduledTasksLabel')}
            </p>
            <div className='space-y-1.5'>
              {tasks.map((tk) => (
                <div
                  key={tk.id}
                  className='flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-900/60 border border-gray-800'
                >
                  <Activity size={13} className='text-green-400 flex-shrink-0' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm text-white'>{tk.name}</p>
                    <p className='text-xs text-gray-500 font-mono'>
                      {tk.id} · {t('fleet.everyMinutesLabel', { min: tk.interval_min })}
                    </p>
                  </div>
                  <code className='text-[10px] text-gray-600 font-mono'>
                    {tk.hash.slice(0, 8)}…
                  </code>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Přeinstalace */}
        <div className='pt-2 border-t border-gray-800'>
          <div className='flex items-center justify-between mb-2'>
            <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide'>
              {t('fleet.reinstallAgentLabel')}
            </p>
            <Button
              size='sm'
              variant='secondary'
              icon={<Download size={12} />}
              loading={genReinstall.isPending}
              onClick={() => genReinstall.mutate()}
            >
              {t('fleet.generateScriptButton')}
            </Button>
          </div>
          {reinstallScripts
            ? (
              <div className='space-y-2'>
                <p className='text-xs text-gray-500'>
                  {t('fleet.oneTimeTokenHint')}
                </p>
                {reinstallScripts.cmd_ps1 && (
                  <div>
                    <p className='text-xs text-gray-400 mb-1 flex items-center gap-1.5'>
                      <Monitor size={11} className='text-violet-400' />{' '}
                      {t('fleet.windowsPsAdminLabel')}
                    </p>
                    <div className='flex items-center gap-2'>
                      <code className='flex-1 text-xs font-mono bg-gray-800/80 text-violet-300 px-3 py-2 rounded-xl break-all'>
                        {reinstallScripts.cmd_ps1}
                      </code>
                      <Button
                        size='icon'
                        variant='ghost'
                        onClick={() => copyText(reinstallScripts.cmd_ps1!)}
                        title={t('fleet.copyTooltip')}
                      >
                        <Copy size={12} />
                      </Button>
                    </div>
                  </div>
                )}
                {reinstallScripts.cmd_sh && (
                  <div>
                    <p className='text-xs text-gray-400 mb-1 flex items-center gap-1.5'>
                      <Terminal size={11} className='text-blue-400' /> {t('fleet.linuxLabel')}
                    </p>
                    <div className='flex items-center gap-2'>
                      <code className='flex-1 text-xs font-mono bg-gray-800/80 text-green-300 px-3 py-2 rounded-xl break-all'>
                        {reinstallScripts.cmd_sh}
                      </code>
                      <Button
                        size='icon'
                        variant='ghost'
                        onClick={() => copyText(reinstallScripts.cmd_sh!)}
                        title={t('fleet.copyTooltip')}
                      >
                        <Copy size={12} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )
            : (
              <p className='text-xs text-gray-600'>
                {t('fleet.clickGenerateScriptHint')}
              </p>
            )}
        </div>
      </div>
    </Modal>
  );
}

function AlloyUninstallModal(
  { agentId, pubUrl, onClose }: { agentId: string; pubUrl: string; onClose: () => void },
) {
  const { t } = useTranslation();
  const win = `irm '${pubUrl}/apiext/fleet/uninstall-alloy-agent.ps1' | iex`;
  const linux = `curl -fsSL '${pubUrl}/apiext/fleet/uninstall-alloy-agent.sh' | bash`;
  return (
    <Modal
      open
      onClose={onClose}
      title={
        <>
          {t('fleet.uninstallAlloyModalTitle')}{' '}
          <span className='font-mono text-blue-400'>{agentId}</span>
        </>
      }
    >
      <div className='space-y-4'>
        <div>
          <p className='text-xs text-gray-400 mb-1.5 flex items-center gap-1.5'>
            <Monitor size={12} className='text-violet-400' /> {t('fleet.windowsPsAdminLabel')}
          </p>
          <div className='flex items-start gap-2'>
            <code className='flex-1 text-xs font-mono bg-gray-800/80 text-violet-300 p-3 rounded-xl break-all'>
              {win}
            </code>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => copyText(win)}
              title={t('fleet.copyTooltip')}
            >
              <Copy size={13} />
            </Button>
          </div>
        </div>
        <div>
          <p className='text-xs text-gray-400 mb-1.5 flex items-center gap-1.5'>
            <Terminal size={12} className='text-blue-400' /> {t('fleet.linuxLabel')}
          </p>
          <div className='flex items-start gap-2'>
            <code className='flex-1 text-xs font-mono bg-gray-800/80 text-green-300 p-3 rounded-xl break-all'>
              {linux}
            </code>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => copyText(linux)}
              title={t('fleet.copyTooltip')}
            >
              <Copy size={13} />
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function AlloyCommandsModal({ agent, onClose }: { agent: AlloyAgent; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const updateConfig = useMutation({
    mutationFn: () =>
      mcpCall('fleet.agent_command', { agent_id: agent.id, command: 'update_config' }),
    onSuccess: () => {
      toast.success(t('fleet.updateConfigSentToast'));
      qc.invalidateQueries({ queryKey: ['fl-alloy'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const restartAlloy = useMutation({
    mutationFn: () =>
      mcpCall('fleet.agent_command', { agent_id: agent.id, command: 'restart_alloy' }),
    onSuccess: () => {
      toast.success(t('fleet.restartAlloySentToast'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const downloadConfig = () => {
    const url = `/apiext/fleet/alloy-agents/${agent.id}/alloy-config`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `alloy-config-${agent.id}.river`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <>
          {t('fleet.commandsModalTitle')}{' '}
          <span className='font-mono text-blue-400'>{agent.id}</span>
        </>
      }
    >
      <div className='space-y-3'>
        <Button
          variant='secondary'
          icon={<FileText size={14} />}
          onClick={downloadConfig}
          className='w-full justify-start'
        >
          {t('fleet.downloadConfigButton')}
        </Button>
        <Button
          variant='secondary'
          icon={<RefreshCw size={14} />}
          loading={updateConfig.isPending}
          onClick={() => updateConfig.mutate()}
          className='w-full justify-start'
        >
          {t('fleet.updateConfigButton')}
        </Button>
        <Button
          variant='warning'
          icon={<Activity size={14} />}
          loading={restartAlloy.isPending}
          onClick={() => restartAlloy.mutate()}
          className='w-full justify-start'
        >
          {t('fleet.restartAlloyButton')}
        </Button>
      </div>
    </Modal>
  );
}

function AlloyAgentsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editAgent, setEditAgent] = useState<AlloyAgent | null>(null);
  const [uninstallAgent, setUninstallAgent] = useState<AlloyAgent | null>(null);
  const [commandAgent, setCommandAgent] = useState<AlloyAgent | null>(null);

  const { data: agents = [], isLoading, refetch } = useQuery({
    queryKey: ['fl-alloy'],
    queryFn: () => mcpCall<AlloyAgent[]>('fleet.alloy_list'),
  });
  const { data: customers = [] } = useQuery({
    queryKey: ['fl-customers'],
    queryFn: () => mcpCall<Customer[]>('fleet.customer_list'),
  });
  const { data: serverInfo } = useQuery({
    queryKey: ['fl-server-info'],
    queryFn: () => mcpCall<{ pub_url?: string; agent_url: string }>('fleet.server_info'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      mcpCall('fleet.alloy_update', { agent_id: id, enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fl-alloy'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const filtered = (agents as AlloyAgent[]).filter((a) =>
    !search || a.id.toLowerCase().includes(search.toLowerCase()) ||
    a.hostname?.toLowerCase().includes(search.toLowerCase())
  );

  const pubUrl = serverInfo?.pub_url ?? serverInfo?.agent_url ?? 'https://10.119.251.116:9083';

  return (
    <div className='space-y-5'>
      <div className='flex items-center gap-3'>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t('fleet.searchAgentPlaceholder')}
        />
        <span className='text-sm text-gray-500'>
          {filtered.length} / {(agents as AlloyAgent[]).length}
        </span>
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
      </div>

      {filtered.length === 0 && !isLoading
        ? (
          <EmptyState
            icon={<Monitor className='w-12 h-12 text-gray-700' />}
            title={t('fleet.noAgentsTitle')}
            description={t('fleet.noAlloyAgentsDescription')}
          />
        )
        : (
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'>
            {filtered.map((a) => {
              const cust = a.customer ??
                (customers as Customer[]).find((c) => c.id === a.customer_id);
              const agentStatus = a.status ?? (a.enabled !== false ? 'online' : 'offline');
              return (
                <Card key={a.id} className={a.enabled === false ? 'opacity-60' : ''}>
                  <CardContent className='p-4 space-y-3'>
                    <div className='flex items-start justify-between'>
                      <div className='min-w-0 flex-1'>
                        <p className='font-mono text-sm text-white font-semibold truncate'>
                          {a.id}
                        </p>
                        <p className='text-xs text-gray-500 truncate'>{a.hostname || '—'}</p>
                        {cust && (
                          <span
                            className='inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium mt-1'
                            style={{
                              background: (cust.color ?? '#6c63ff') + '22',
                              color: cust.color ?? '#6c63ff',
                            }}
                          >
                            {cust.label}
                          </span>
                        )}
                      </div>
                      <AsyncStatusToggle
                        value={a.enabled !== false}
                        pending={toggle.isPending}
                        onToggle={(v) => toggle.mutateAsync({ id: a.id, enabled: v })}
                        onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
                      />
                    </div>

                    {/* Status & heartbeat */}
                    <div className='flex items-center justify-between text-xs text-gray-500'>
                      <StatusDot status={agentStatus} withLabel />
                      {a.last_heartbeat && (
                        <span title={t('fleet.lastHeartbeatTooltip')}>
                          {fmtDate(a.last_heartbeat)}
                        </span>
                      )}
                    </div>

                    {a.roles && a.roles.length > 0 && (
                      <div className='flex flex-wrap gap-1'>
                        {a.roles.map((r) => <Badge key={r} variant='gray'>{r}</Badge>)}
                      </div>
                    )}

                    {/* Actions */}
                    <div className='flex gap-1.5 pt-1 border-t border-gray-800'>
                      <Button
                        size='sm'
                        variant='ghost'
                        icon={<Settings size={11} />}
                        onClick={() => setEditAgent(a)}
                        className='flex-1 justify-center'
                      >
                        {t('fleet.settingsButton')}
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        icon={<Terminal size={11} />}
                        onClick={() => setCommandAgent(a)}
                        className='px-2'
                        title={t('fleet.commandsTooltip')}
                      />
                      <Button
                        size='sm'
                        variant='ghost'
                        icon={<Trash2 size={11} />}
                        onClick={() => setUninstallAgent(a)}
                        className='px-2 text-gray-600 hover:text-red-400'
                        title={t('fleet.uninstallTooltip')}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      {editAgent && (
        <AlloyEditModal
          agent={editAgent}
          customers={customers as Customer[]}
          onClose={() => setEditAgent(null)}
        />
      )}
      {uninstallAgent && (
        <AlloyUninstallModal
          agentId={uninstallAgent.id}
          pubUrl={pubUrl}
          onClose={() => setUninstallAgent(null)}
        />
      )}
      {commandAgent && (
        <AlloyCommandsModal
          agent={commandAgent}
          onClose={() => setCommandAgent(null)}
        />
      )}
    </div>
  );
}

// ── Enrollment ────────────────────────────────────────────────────────────────

function InstallModal({ token, onClose }: { token: FleetToken; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      open
      onClose={onClose}
      title={t('fleet.deploymentModalTitle', { label: token.label })}
      footer={
        <div className='flex items-center justify-between text-xs text-gray-500 w-full'>
          <span>{t('fleet.expiresLabel')} {fmtDate(token.expires_at)}</span>
          {token.max_uses != null && (
            <span>
              {t('fleet.usedLabel', {
                count: token.use_count ?? 0,
                max: token.max_uses < 0 ? '∞' : token.max_uses,
              })}
            </span>
          )}
        </div>
      }
    >
      <div className='space-y-4'>
        {token.install_sh && (
          <div>
            <p className='text-xs text-gray-400 mb-1.5 flex items-center gap-1.5'>
              <Terminal size={12} className='text-blue-400' /> {t('fleet.linuxMacRootHint')}
            </p>
            <div className='flex items-start gap-2'>
              <code className='flex-1 text-xs font-mono bg-gray-800/80 text-green-300 p-3 rounded-xl break-all'>
                {token.install_sh}
              </code>
              <Button
                size='icon'
                variant='ghost'
                onClick={() => copyText(token.install_sh!)}
                title={t('fleet.copyTooltip')}
              >
                <Copy size={13} />
              </Button>
            </div>
          </div>
        )}
        {token.install_ps1 && (
          <div>
            <p className='text-xs text-gray-400 mb-1.5 flex items-center gap-1.5'>
              <Monitor size={12} className='text-violet-400' /> {t('fleet.windowsPsAdminLabel')}
            </p>
            <div className='flex items-start gap-2'>
              <code className='flex-1 text-xs font-mono bg-gray-800/80 text-violet-300 p-3 rounded-xl break-all'>
                {token.install_ps1}
              </code>
              <Button
                size='icon'
                variant='ghost'
                onClick={() => copyText(token.install_ps1!)}
                title={t('fleet.copyTooltip')}
              >
                <Copy size={13} />
              </Button>
            </div>
          </div>
        )}
        {!token.install_sh && !token.install_ps1 && (
          <p className='text-sm text-gray-500 py-4 text-center'>
            {t('fleet.noInstallScriptMessage')}
          </p>
        )}
      </div>
    </Modal>
  );
}

function UninstallModal({ pubUrl, onClose }: { pubUrl: string; onClose: () => void }) {
  const { t } = useTranslation();
  const linux = `curl -fsSL '${pubUrl}/apiext/fleet/uninstall-alloy-agent.sh' | bash`;
  const win = `irm '${pubUrl}/apiext/fleet/uninstall-alloy-agent.ps1' | iex`;
  return (
    <Modal
      open
      onClose={onClose}
      title={t('fleet.uninstallFleetAgentModalTitle')}
    >
      <div className='space-y-4'>
        <div>
          <p className='text-xs text-gray-400 mb-1.5 flex items-center gap-1.5'>
            <Terminal size={12} className='text-blue-400' /> {t('fleet.linuxMacRootHintParen')}
          </p>
          <div className='flex items-start gap-2'>
            <code className='flex-1 text-xs font-mono bg-gray-800/80 text-green-300 p-3 rounded-xl break-all'>
              {linux}
            </code>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => copyText(linux)}
              title={t('fleet.copyTooltip')}
            >
              <Copy size={13} />
            </Button>
          </div>
        </div>
        <div>
          <p className='text-xs text-gray-400 mb-1.5 flex items-center gap-1.5'>
            <Monitor size={12} className='text-violet-400' /> {t('fleet.windowsPsAdminLabel')}
          </p>
          <div className='flex items-start gap-2'>
            <code className='flex-1 text-xs font-mono bg-gray-800/80 text-violet-300 p-3 rounded-xl break-all'>
              {win}
            </code>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => copyText(win)}
              title={t('fleet.copyTooltip')}
            >
              <Copy size={13} />
            </Button>
          </div>
        </div>
        <p className='text-xs text-gray-500'>
          {t('fleet.permanentLinkLabel')}{' '}
          <code className='text-blue-400 text-xs break-all'>
            {pubUrl}/apiext/fleet/uninstall-alloy-agent.sh
          </code>
        </p>
      </div>
    </Modal>
  );
}

// Typ nasazení — rozlišení pro co je token
const ENROLL_PURPOSES = [
  {
    value: 'alloy',
    label: 'Alloy agent (primární)',
    desc: 'Alloy/promtail na sdíleném nebo zákaznickém serveru — sbírá Windows Event Log, syslog',
    color: 'text-blue-400',
    badgeVariant: 'blue' as const,
  },
  {
    value: 'full',
    label: 'Fleet agent (full)',
    desc: 'Individuální agent: logy + security (RDS/KMS) + WireGuard VPN + RustDesk',
    color: 'text-purple-400',
    badgeVariant: 'purple' as const,
  },
];

const ENROLL_PURPOSE_KEYS: Record<string, { label: string; desc: string }> = {
  alloy: { label: 'enrollPurposeAlloyLabel', desc: 'enrollPurposeAlloyDesc' },
  full: { label: 'enrollPurposeFullLabel', desc: 'enrollPurposeFullDesc' },
};

function FleetSettingsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: ['fl-settings'],
    queryFn: () => mcpCall<Record<string, string>>('fleet.settings_get'),
  });

  const [pubUrl, setPubUrl] = useState('');
  const [agentPubUrl, setAgentPubUrl] = useState('');
  const [lokiUrl, setLokiUrl] = useState('');
  const [loaded, setLoaded] = useState(false);

  if (settings && !loaded) {
    setPubUrl(settings['pub_url'] ?? '');
    setAgentPubUrl(settings['agent_pub_url'] ?? '');
    setLokiUrl(settings['loki_url'] ?? '');
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: () =>
      mcpCall('fleet.settings_set', {
        pub_url: pubUrl,
        agent_pub_url: agentPubUrl,
        loki_url: lokiUrl,
      }),
    onSuccess: () => {
      toast.success(t('fleet.settingsSavedToast'));
      qc.invalidateQueries({ queryKey: ['fl-server-info'] });
      qc.invalidateQueries({ queryKey: ['fl-settings'] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const inp =
    'w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm font-mono focus:outline-none focus:border-blue-500';

  return (
    <Modal
      open
      onClose={onClose}
      title={t('fleet.fleetSettingsModalTitle')}
      footer={
        <div className='flex gap-3'>
          <Button variant='secondary' onClick={onClose} className='flex-1'>
            {t('fleet.cancelButton')}
          </Button>
          <Button
            variant='primary'
            onClick={() => save.mutate()}
            loading={save.isPending}
            className='flex-1'
          >
            {t('fleet.saveButton')}
          </Button>
        </div>
      }
    >
      <div className='space-y-4'>
        <div className='p-3 rounded-xl bg-blue-600/5 border border-blue-500/20 text-xs text-blue-300 space-y-1'>
          <p className='font-semibold'>{t('fleet.publicUrlsHint')}</p>
          <p className='text-gray-400 font-mono'>
            {t('fleet.acmPublicUrlPortHint', 'acm.services.maxcloud.cz → port {{port}} (API + Dashboard)', { port: DEFAULT_API_PORT })}
          </p>
          <p className='text-gray-400 font-mono'>
            {t('fleet.agentPublicUrlPortHint', 'acm-agent.services.maxcloud.cz → port {{port}} (Agent API) + /loki/ → 3100', { port: DEFAULT_AGENT_PORT })}
          </p>
        </div>
        <div>
          <label className='text-xs text-gray-400 mb-1 block'>
            {t('fleet.acmPublicUrlLabel')}{' '}
            <span className='text-gray-600'>{t('fleet.installUninstallHint')}</span>
          </label>
          <input
            value={pubUrl}
            onChange={(e) => setPubUrl(e.target.value)}
            className={inp}
            placeholder='https://acm.services.maxcloud.cz'
          />
        </div>
        <div>
          <label className='text-xs text-gray-400 mb-1 block'>
            {t('fleet.agentApiPublicUrlLabel')}{' '}
            <span className='text-gray-600'>{t('fleet.enrollmentHeartbeatConfigHint')}</span>
          </label>
          <input
            value={agentPubUrl}
            onChange={(e) => setAgentPubUrl(e.target.value)}
            className={inp}
            placeholder='https://acm-agent.services.maxcloud.cz'
          />
        </div>
        <div>
          <label className='text-xs text-gray-400 mb-1 block'>
            {t('fleet.lokiPushUrlLabel')}{' '}
            <span className='text-gray-600'>{t('fleet.alloyConfigHint')}</span>
          </label>
          <input
            value={lokiUrl}
            onChange={(e) => setLokiUrl(e.target.value)}
            className={inp}
            placeholder='https://acm-agent.services.maxcloud.cz/loki/api/v1/push'
          />
        </div>
      </div>
    </Modal>
  );
}

function EnrollTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showUninstall, setShowUninstall] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedToken, setSelectedToken] = useState<FleetToken | null>(null);
  const [newToken, setNewToken] = useState<FleetToken | null>(null);

  const { data: tokens = [], isLoading, refetch } = useQuery({
    queryKey: ['fl-enrollments'],
    queryFn: () => mcpCall<FleetToken[]>('fleet.enrollment_list'),
    refetchInterval: 60_000,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['fl-customers'],
    queryFn: () => mcpCall<Customer[]>('fleet.customer_list'),
  });
  const { data: serverInfo } = useQuery({
    queryKey: ['fl-server-info'],
    queryFn: () => mcpCall<{ pub_url?: string; agent_url: string }>('fleet.server_info'),
  });

  const pubUrl = serverInfo?.pub_url ?? serverInfo?.agent_url ?? 'https://10.119.251.116:9083';

  const { data: availableTemplates = [] } = useQuery({
    queryKey: ['fl-templates'],
    queryFn: () => mcpCall<Template[]>('fleet.template_list'),
  });
  const [enrollTemplates, setEnrollTemplates] = useState<string[]>(['heartbeat']);

  const create = useMutation({
    mutationFn: (
      d: {
        label: string;
        agent_type: string;
        ttl_hours: number;
        max_uses?: number;
        customer_id?: string;
        group_name?: string;
        template_ids?: string[];
      },
    ) => mcpCall<{ ok: boolean; token: FleetToken }>('fleet.enrollment_create', d),
    onSuccess: (r) => {
      setNewToken(r.token);
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['fl-enrollments'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: (token: string) => mcpCall('fleet.enrollment_revoke', { token }),
    onSuccess: () => {
      toast.success(t('fleet.tokenRevokedToast'));
      qc.invalidateQueries({ queryKey: ['fl-enrollments'] });
    },
  });

  const tokenState = (tk: FleetToken) => {
    if (tk.revoked) return { label: t('fleet.tokenRevokedLabel'), variant: 'red' as const };
    if (tk.used) return { label: t('fleet.tokenUsedLabel'), variant: 'green' as const };
    if (new Date(tk.expires_at) < new Date()) {
      return { label: t('fleet.tokenExpiredLabel'), variant: 'gray' as const };
    }
    return { label: t('fleet.tokenActiveLabel'), variant: 'blue' as const };
  };

  const purposeLabel = (type: string) => {
    const p = ENROLL_PURPOSES.find((p) => p.value === type);
    if (!p) return type;
    const keys = ENROLL_PURPOSE_KEYS[type];
    return keys ? t(`fleet.${keys.label}`) : p.label;
  };

  return (
    <div className='space-y-5'>
      <div className='flex items-center justify-between'>
        <div>
          <h3 className='text-base font-semibold text-white'>{t('fleet.deployAgentsHeader')}</h3>
          <p className='text-sm text-gray-500 mt-0.5'>
            {t('fleet.tokensCountAgentUrlLabel', { count: (tokens as FleetToken[]).length })}{' '}
            <code className='text-blue-400 text-xs'>{pubUrl}</code>
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => setShowSettings(true)}
            title={t('fleet.fleetSettingsModalTitle')}
          >
            <Settings size={15} />
          </Button>
          <Button
            variant='secondary'
            size='sm'
            icon={<Download size={13} />}
            onClick={() => setShowUninstall(true)}
          >
            {t('fleet.uninstallButton')}
          </Button>
          <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
          <Button
            variant='primary'
            size='sm'
            icon={<Plus size={14} />}
            onClick={() => setShowCreate(true)}
          >
            {t('fleet.newTokenButton')}
          </Button>
        </div>
      </div>

      {newToken && (
        <div className='rounded-2xl border border-emerald-500/20 bg-emerald-600/5 p-4 space-y-3'>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-semibold text-emerald-400'>
                {t('fleet.tokenCreatedLabel', { label: newToken.label })}
              </p>
              <p className='text-xs text-gray-500'>
                {t('fleet.purposeExpiresLabel', {
                  purpose: purposeLabel(newToken.agent_type),
                  date: fmtDate(newToken.expires_at),
                })}
              </p>
            </div>
            <Button
              size='icon'
              variant='ghost'
              onClick={() => setNewToken(null)}
              title={t('fleet.closeTooltip')}
            >
              <X size={14} />
            </Button>
          </div>
          {newToken.install_sh && (
            <div>
              <p className='text-xs text-gray-400 mb-1 flex items-center gap-1.5'>
                <Terminal size={11} className='text-blue-400' /> {t('fleet.linuxColonLabel')}
              </p>
              <div className='flex items-start gap-2'>
                <code className='flex-1 text-xs font-mono bg-gray-800/80 text-green-300 p-2 rounded-lg break-all'>
                  {newToken.install_sh}
                </code>
                <Button
                  size='icon'
                  variant='ghost'
                  onClick={() => copyText(newToken.install_sh!)}
                  title={t('fleet.copyTooltip')}
                >
                  <Copy size={12} />
                </Button>
              </div>
            </div>
          )}
          {newToken.install_ps1 && (
            <div>
              <p className='text-xs text-gray-400 mb-1 flex items-center gap-1.5'>
                <Monitor size={11} className='text-violet-400' /> {t('fleet.windowsColonLabel')}
              </p>
              <div className='flex items-start gap-2'>
                <code className='flex-1 text-xs font-mono bg-gray-800/80 text-violet-300 p-2 rounded-lg break-all'>
                  {newToken.install_ps1}
                </code>
                <Button
                  size='icon'
                  variant='ghost'
                  onClick={() => copyText(newToken.install_ps1!)}
                  title={t('fleet.copyTooltip')}
                >
                  <Copy size={12} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Card>
        <CardContent>
          {isLoading
            ? <p className='text-sm text-gray-500 py-4'>{t('fleet.loadingLabel')}</p>
            : (tokens as FleetToken[]).length === 0
            ? (
              <EmptyState
                icon={<Shield className='w-12 h-12 text-gray-700' />}
                title={t('fleet.noTokensTitle')}
                description={t('fleet.noTokensDescription')}
              />
            )
            : (
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-gray-800'>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>
                      {t('fleet.colLabel')}
                    </th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>
                      {t('fleet.colPurpose')}
                    </th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>
                      {t('fleet.colCustomer')}
                    </th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>
                      {t('fleet.colExpires')}
                    </th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>
                      {t('fleet.colUsage')}
                    </th>
                    <th className='text-left py-3 px-4 text-xs text-gray-400'>
                      {t('fleet.colState')}
                    </th>
                    <th className='py-3 px-4 w-20' />
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-800'>
                  {(tokens as FleetToken[]).map((tk) => {
                    const st = tokenState(tk);
                    const active = !tk.revoked && !tk.used && new Date(tk.expires_at) > new Date();
                    const purpose = ENROLL_PURPOSES.find((p) => p.value === tk.agent_type);
                    return (
                      <tr key={tk.hash} className='group'>
                        <td className='py-3 px-4'>
                          <p className='text-white'>{tk.label}</p>
                          <code className='text-xs text-gray-600 font-mono'>
                            {tk.hash.slice(0, 12)}…
                          </code>
                        </td>
                        <td className='py-3 px-4'>
                          <Badge variant={purpose?.badgeVariant ?? 'gray'}>
                            {purpose ? purposeLabel(tk.agent_type) : tk.agent_type}
                          </Badge>
                        </td>
                        <td className='py-3 px-4 text-xs text-gray-400'>{tk.customer_id || '—'}</td>
                        <td className='py-3 px-4 text-xs text-gray-400'>
                          {fmtDate(tk.expires_at)}
                        </td>
                        <td className='py-3 px-4 text-xs text-gray-400'>
                          {tk.max_uses != null
                            ? `${tk.use_count ?? 0} / ${tk.max_uses < 0 ? '∞' : tk.max_uses}`
                            : '—'}
                        </td>
                        <td className='py-3 px-4'>
                          <Badge variant={st.variant}>{st.label}</Badge>
                        </td>
                        <td className='py-3 px-4'>
                          <InlineActions.Row
                            onDelete={() => revoke.mutate(tk.hash)}
                            deleteConfirm
                          >
                            {active && (
                              <InlineActions.IconBtn
                                icon={<Terminal size={13} />}
                                title={t('fleet.installScriptsTooltip')}
                                onClick={() => setSelectedToken(tk)}
                              />
                            )}
                          </InlineActions.Row>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>

      {showCreate && (
        <Modal
          open
          onClose={() => {
            setShowCreate(false);
            setEnrollTemplates(['heartbeat']);
          }}
          title={t('fleet.newEnrollmentTokenModalTitle')}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const f = e.target as HTMLFormElement;
              const label = (f.elements.namedItem('label') as HTMLInputElement).value.trim();
              const agent_type = (f.elements.namedItem('agent_type') as HTMLSelectElement).value;
              const ttl_hours = parseInt((f.elements.namedItem('ttl') as HTMLSelectElement).value);
              const max_uses_raw = parseInt(
                (f.elements.namedItem('max_uses') as HTMLSelectElement).value,
              );
              const max_uses = isNaN(max_uses_raw) ? undefined : max_uses_raw;
              const customer_id =
                (f.elements.namedItem('customer_id') as HTMLSelectElement).value || undefined;
              const group_name =
                (f.elements.namedItem('group_name') as HTMLInputElement).value.trim() || undefined;
              if (!label) return toast.error(t('fleet.enterLabelError'));
              create.mutate({
                label,
                agent_type,
                ttl_hours,
                max_uses,
                customer_id,
                group_name,
                template_ids: enrollTemplates,
              });
            }}
          >
            <div className='space-y-4 mb-6'>
              <div>
                <label className='text-xs text-gray-400 mb-1 block'>
                  {t('fleet.labelMachineNameRequired')}
                </label>
                <input
                  name='label'
                  required
                  placeholder={t('fleet.labelPlaceholderExample')}
                  className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500'
                />
              </div>

              <div>
                <label className='text-xs text-gray-400 mb-2 block'>
                  {t('fleet.deploymentTypeLabel')}
                </label>
                <div className='space-y-2'>
                  {ENROLL_PURPOSES.map((p) => (
                    <label
                      key={p.value}
                      className='flex items-start gap-3 p-3 rounded-xl border border-gray-700 cursor-pointer hover:border-gray-600 has-[:checked]:border-blue-500/40 has-[:checked]:bg-blue-600/5'
                    >
                      <input
                        type='radio'
                        name='agent_type'
                        value={p.value}
                        defaultChecked={p.value === 'alloy'}
                        className='mt-0.5 accent-blue-500'
                      />
                      <div>
                        <p className={`text-sm font-medium ${p.color}`}>{purposeLabel(p.value)}</p>
                        <p className='text-xs text-gray-500 mt-0.5'>
                          {t(`fleet.${ENROLL_PURPOSE_KEYS[p.value]?.desc ?? ''}`)}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {(availableTemplates as Template[]).length > 0 && (
                <div>
                  <label className='text-xs text-gray-400 mb-2 block'>
                    {t('fleet.configTemplatesLabel')}
                  </label>
                  <div className='space-y-1.5'>
                    <label className='flex items-center gap-2.5 p-2.5 rounded-lg bg-gray-800/50 border border-gray-700/50 cursor-not-allowed opacity-60'>
                      <input type='checkbox' checked readOnly className='accent-blue-500' />
                      <div>
                        <p className='text-xs font-medium text-blue-300'>
                          {t('fleet.heartbeatLabel')}
                        </p>
                        <p className='text-xs text-gray-500'>
                          {t('fleet.baseCheckinAlwaysActiveHint')}
                        </p>
                      </div>
                    </label>
                    {(availableTemplates as Template[]).map((tpl) => (
                      <label
                        key={tpl.id}
                        className='flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-700 cursor-pointer hover:border-gray-600 has-[:checked]:border-blue-500/30 has-[:checked]:bg-blue-600/5'
                      >
                        <input
                          type='checkbox'
                          className='accent-blue-500'
                          checked={enrollTemplates.includes(tpl.id)}
                          onChange={(e) =>
                            setEnrollTemplates((prev) =>
                              e.target.checked
                                ? [...prev, tpl.id]
                                : prev.filter((id) => id !== tpl.id)
                            )}
                        />
                        <div className='flex-1 min-w-0'>
                          <p className='text-xs font-medium text-white'>{tpl.label}</p>
                          {tpl.description && (
                            <p className='text-xs text-gray-500 mt-0.5 truncate'>
                              {tpl.description}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='text-xs text-gray-400 mb-1 block'>
                    {t('fleet.customerLabel')}
                  </label>
                  <Select
                    name='customer_id'
                    className='w-full'
                  >
                    <option value=''>{t('fleet.noCustomerOption')}</option>
                    {(customers as Customer[]).map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className='text-xs text-gray-400 mb-1 block'>
                    {t('fleet.groupOptionalLabel')}
                  </label>
                  <input
                    name='group_name'
                    placeholder='servers-prod'
                    className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500'
                  />
                </div>
              </div>

              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='text-xs text-gray-400 mb-1 block'>
                    {t('fleet.tokenValidityLabel')}
                  </label>
                  <Select
                    name='ttl'
                    className='w-full'
                  >
                    <option value='24'>{t('fleet.hours24Option')}</option>
                    <option value='48'>{t('fleet.hours48Option')}</option>
                    <option value='168'>{t('fleet.days7Option')}</option>
                    <option value='720'>{t('fleet.days30Option')}</option>
                  </Select>
                </div>
                <div>
                  <label className='text-xs text-gray-400 mb-1 block'>
                    {t('fleet.maxUsageLabel')}
                  </label>
                  <Select
                    name='max_uses'
                    className='w-full'
                  >
                    <option value='1'>{t('fleet.use1Option')}</option>
                    <option value='3'>{t('fleet.use3Option')}</option>
                    <option value='10'>{t('fleet.use10Option')}</option>
                    <option value='-1'>{t('fleet.unlimitedOption')}</option>
                  </Select>
                </div>
              </div>
            </div>
            <div className='flex gap-3'>
              <Button
                type='button'
                variant='secondary'
                onClick={() => {
                  setShowCreate(false);
                  setEnrollTemplates(['heartbeat']);
                }}
                className='flex-1'
              >
                {t('fleet.cancelButton')}
              </Button>
              <Button
                type='submit'
                variant='primary'
                loading={create.isPending}
                disabled={create.isPending}
                className='flex-1'
              >
                {t('fleet.createTokenButton')}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {selectedToken && (
        <InstallModal
          token={selectedToken}
          onClose={() => setSelectedToken(null)}
        />
      )}
      {showUninstall && <UninstallModal pubUrl={pubUrl} onClose={() => setShowUninstall(false)} />}
      {showSettings && <FleetSettingsModal onClose={() => setShowSettings(false)} />}
    </div>
  );
}

// ── SPLA ──────────────────────────────────────────────────────────────────────
// Microsoft SPLA (Service Provider License Agreement) — konsolidovaný měsíční report
// RDS User SAL + SQL Server (Core / User CAL) + KMS (referenční tracking aktivací)

function SplaTab() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(curMonth);
  const qc = useQueryClient();

  const { data: months = [] } = useQuery({
    queryKey: ['fl-spla-months'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; months: SplaMonth[] }>('fleet.spla', {
        action: 'months',
      });
      return r.months ?? [];
    },
  });

  const { data: rdsSummary, isLoading: rdsLoading } = useQuery({
    queryKey: ['fl-spla-rds', month],
    queryFn: () =>
      api.mcp<{ month: string; computer_count: number; computers: SplaComputer[] }>(
        'fleet.spla',
        { action: 'summary', month },
      ),
  });

  const { data: sqlAgents = [] } = useQuery({
    queryKey: ['fl-sql'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; agents: SqlAgent[] }>('fleet.sql', {
        action: 'list',
      });
      return r.agents ?? [];
    },
  });

  const { data: kmsAgents = [] } = useQuery({
    queryKey: ['fl-kms', month],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; agents: KmsAgent[] }>('fleet.kms', {
        action: 'list',
        month,
      });
      return r.agents ?? [];
    },
  });

  const compute = useMutation({
    mutationFn: (m: string) => api.mcp('fleet.spla', { action: 'compute', month: m }),
    onSuccess: () => {
      toast.success(t('fleet.recalculatedToast'));
      qc.invalidateQueries({ queryKey: ['fl-spla-rds', month] });
      qc.invalidateQueries({ queryKey: ['fl-spla-months'] });
      qc.invalidateQueries({ queryKey: ['fl-rds'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Agregované statistiky
  const rdsComputers = rdsSummary?.computers ?? [];
  const totalRdsUsers = rdsComputers.reduce((s, c) => s + (c.unique_users ?? 0), 0);

  const allInstances = (sqlAgents as SqlAgent[]).flatMap((a) =>
    (a.instances ?? []).map((i) => ({ agent: a.agent, i }))
  );
  const coreInstances = allInstances.filter(({ i }) => i.license_type === 'core');
  const calInstances = allInstances.filter(({ i }) => i.license_type === 'user_cal');
  const totalSqlCores = coreInstances.reduce(
    (s, { i }) => s + ((i.cpu_count ?? 0) * (i.cores_per_socket || 1) || (i.cpu_count ?? 0)),
    0,
  );
  const totalSqlCal = calInstances.reduce((s, { i }) => s + (i.unique_user_count ?? 0), 0);
  const totalKmsBillable = (kmsAgents as KmsAgent[]).reduce(
    (s, a) => s + (a.billable_count ?? a.unique_count ?? 0),
    0,
  );

  const monthList = (months as SplaMonth[]).slice(0, 8);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <div>
          <h3 className='text-base font-semibold text-white'>
            {t('fleet.splaMonthlyReportHeader')}
          </h3>
          <p className='text-xs text-gray-500 mt-0.5'>
            {t('fleet.splaFullNameLabel')} {month}
          </p>
        </div>
        <div className='flex items-center gap-2 flex-wrap'>
          {monthList.map((m) => (
            <Button
              key={m.month}
              size='sm'
              variant={month === m.month ? 'primary' : 'ghost'}
              onClick={() => setMonth(m.month)}
            >
              {m.month}
            </Button>
          ))}
          <Button
            size='sm'
            variant='secondary'
            icon={<RefreshCw size={12} className={compute.isPending ? 'animate-spin' : ''} />}
            onClick={() => compute.mutate(month)}
            loading={compute.isPending}
          >
            {t('fleet.recalculateButton')}
          </Button>
        </div>
      </div>

      {/* Souhrn licencí */}
      <StatsGrid columns={4}>
        <StatCard
          icon={Activity}
          label={t('fleet.statRdsUserSal')}
          value={totalRdsUsers}
          sub={t('fleet.uniqueUsersSubLabel', { month })}
          variant='info'
        />
        <StatCard
          icon={HardDrive}
          label='SQL Core'
          value={totalSqlCores}
          sub={t('fleet.coresSubLabel')}
        />
        <StatCard
          icon={Database}
          label='SQL User CAL'
          value={totalSqlCal}
          sub={t('fleet.uniqueUsersSubLabelPlain')}
        />
        <StatCard
          icon={Lock}
          label={t('fleet.statKmsActivations')}
          value={totalKmsBillable}
          sub={t('fleet.billableClientsSubLabel')}
          variant='success'
        />
      </StatsGrid>

      {/* RDS */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <CardTitle icon={Activity}>{t('fleet.rdsFullTitle')}</CardTitle>
            <span className='text-xs text-gray-500'>
              {t('fleet.serverCountLabel', { count: rdsComputers.length })}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {rdsLoading
            ? <p className='text-sm text-gray-500 py-4'>{t('fleet.loadingLabel')}</p>
            : rdsComputers.length === 0
            ? (
              <EmptyState
                icon={<Activity className='w-10 h-10 text-gray-700' />}
                title={t('fleet.noRdsDataTitle')}
                description={t('fleet.noRdsLoginsDescription')}
              />
            )
            : (
              <table className='w-full text-sm'>
                <thead>
                  <tr className='border-b border-gray-800'>
                    <th className='text-left py-3 text-xs text-gray-400'>Server</th>
                    <th className='text-right py-3 text-xs text-gray-400 pr-6'>
                      {t('fleet.colUniqueUsersRdsSal')}
                    </th>
                    <th className='text-right py-3 text-xs text-gray-400'>
                      {t('fleet.colTotalLogins')}
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-800/40'>
                  {rdsComputers.map((c) => (
                    <tr key={c.computer}>
                      <td className='py-2.5 text-white font-mono text-xs'>{c.computer}</td>
                      <td className='py-2.5 text-right pr-6'>
                        <Badge variant={c.unique_users > 0 ? 'blue' : 'gray'}>
                          {c.unique_users ?? 0}
                        </Badge>
                      </td>
                      <td className='py-2.5 text-right text-gray-400 text-xs'>
                        {(c.event_count ?? 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {rdsComputers.length > 0 && (
                    <tr className='border-t border-gray-700'>
                      <td className='py-2.5 text-gray-400 text-xs font-medium'>
                        {t('fleet.totalSalLabel')}
                      </td>
                      <td className='py-2.5 text-right pr-6 text-white font-bold'>
                        {totalRdsUsers}
                      </td>
                      <td />
                    </tr>
                  )}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>

      {/* SQL Server */}
      {allInstances.filter(({ i }) => i.license_type !== 'free').length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle icon={Database}>SQL Server</CardTitle>
          </CardHeader>
          <CardContent>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-800'>
                  <th className='text-left py-3 text-xs text-gray-400'>
                    {t('fleet.colInstanceAgent')}
                  </th>
                  <th className='text-left py-3 text-xs text-gray-400'>Edition</th>
                  <th className='text-left py-3 text-xs text-gray-400'>
                    {t('fleet.colLicenseType')}
                  </th>
                  <th className='text-right py-3 text-xs text-gray-400'>{t('fleet.colCores')}</th>
                  <th className='text-right py-3 text-xs text-gray-400'>
                    {t('fleet.colUniqueUsers')}
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/40'>
                {allInstances
                  .filter(({ i }) => i.license_type !== 'free')
                  .map(({ agent, i }) => (
                    <tr key={`${agent}-${i.instance}`}>
                      <td className='py-2.5'>
                        <p className='text-white font-mono text-xs'>{i.instance}</p>
                        <p className='text-xs text-gray-500'>{agent}</p>
                      </td>
                      <td className='py-2.5 text-gray-400 text-xs'>{i.edition ?? '—'}</td>
                      <td className='py-2.5'>
                        <Badge variant={i.license_type === 'core' ? 'blue' : 'purple'}>
                          {i.license_type === 'core' ? 'Core' : 'User CAL'}
                        </Badge>
                      </td>
                      <td className='py-2.5 text-right text-xs'>
                        {i.license_type === 'core'
                          ? (
                            <span className='text-white font-medium'>
                              {((i.cpu_count ?? 0) * (i.cores_per_socket || 1)) ||
                                (i.cpu_count ?? 0)}
                            </span>
                          )
                          : <span className='text-gray-600'>—</span>}
                      </td>
                      <td className='py-2.5 text-right text-xs'>
                        {i.license_type === 'user_cal'
                          ? (
                            <span className='text-white font-medium'>
                              {i.unique_user_count ?? 0}
                            </span>
                          )
                          : <span className='text-gray-600'>—</span>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* KMS — referenční tracking */}
      {(kmsAgents as KmsAgent[]).length > 0 && (
        <Card>
          <CardHeader>
            <div className='flex items-center justify-between'>
              <CardTitle icon={Lock}>{t('fleet.kmsFullTitle')}</CardTitle>
              <Badge variant='gray'>{t('fleet.notDirectSplaFeeBadge')}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <table className='w-full text-sm'>
              <thead>
                <tr className='border-b border-gray-800'>
                  <th className='text-left py-3 text-xs text-gray-400'>
                    {t('fleet.colKmsServer')}
                  </th>
                  <th className='text-right py-3 text-xs text-gray-400'>
                    {t('fleet.colUniqueClients')}
                  </th>
                  <th className='text-right py-3 text-xs text-gray-400'>Billable</th>
                  <th className='text-right py-3 text-xs text-gray-400'>
                    {t('fleet.colActivationsThisMonth')}
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-800/40'>
                {(kmsAgents as KmsAgent[]).map((a) => (
                  <tr key={a.agent}>
                    <td className='py-2.5 text-white font-mono text-xs'>{a.agent}</td>
                    <td className='py-2.5 text-right text-gray-300'>{a.unique_count ?? 0}</td>
                    <td className='py-2.5 text-right'>
                      <Badge variant='green'>{a.billable_count ?? a.unique_count ?? 0}</Badge>
                    </td>
                    <td className='py-2.5 text-right text-gray-400 text-xs'>
                      {(a.event_count ?? 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Logs ──────────────────────────────────────────────────────────────────────

const LOG_RANGES = [{ h: 1, l: '1h' }, { h: 6, l: '6h' }, { h: 24, l: '24h' }, {
  h: 168,
  l: '7d',
}] as const;

function LogsTab() {
  const { t } = useTranslation();
  const { data: customers = [] } = useQuery({
    queryKey: ['fl-customers'],
    queryFn: () => mcpCall<Customer[]>('fleet.customer_list'),
  });
  const { data: labels } = useQuery({
    queryKey: ['fl-log-labels'],
    queryFn: () =>
      api.mcp<{ ok: boolean; channels: string[]; computers: string[] }>('fleet.logs', {
        action: 'labels',
      }),
  });

  const [range, setRange] = useState(1);
  const [search, setSearch] = useState('');
  const [custFilter, setCustFilter] = useState('');
  const [chanFilter, setChanFilter] = useState('');
  const [computerFilter, setComputerFilter] = useState('');
  const [query, setQuery] = useState({
    range: 1,
    search: '',
    customer: '',
    channel: '',
    computer: '',
  });

  const { data: logs, isLoading, refetch } = useQuery({
    queryKey: ['fl-logs', query],
    queryFn: async () => {
      const args: Record<string, unknown> = {
        action: 'query',
        range: query.range,
      };
      if (query.search) args.search = query.search;
      if (query.customer) args.customer = query.customer;
      if (query.channel) args.channel = query.channel;
      if (query.computer) args.computer = query.computer;
      return api.mcp<FleetLogsResponse>('fleet.logs', args);
    },
  });

  const doSearch = useCallback(
    () =>
      setQuery({
        range,
        search,
        customer: custFilter,
        channel: chanFilter,
        computer: computerFilter,
      }),
    [range, search, custFilter, chanFilter, computerFilter],
  );

  return (
    <div className='space-y-5'>
      <div className='flex items-center gap-3 flex-wrap'>
        <h3 className='text-base font-semibold text-white flex-1'>Log Viewer</h3>
        {LOG_RANGES.map((r) => (
          <Button
            key={r.h}
            size='sm'
            variant={range === r.h ? 'primary' : 'secondary'}
            onClick={() => setRange(r.h)}
          >
            {r.l}
          </Button>
        ))}
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
      </div>

      <div className='flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-900/40 px-4 py-3 text-xs'>
        <Activity
          size={15}
          className={logs?.metrics.available ? 'text-green-400' : 'text-amber-400'}
        />
        <span className='text-gray-300'>{t('fleet.metricsModul', 'Metrics modul')}</span>
        {logs?.metrics.available
          ? (
            <Badge variant='green'>
              propojeno · {logs.metrics.nodes.length} {t('fleet.aktivnichHostu', 'aktivních hostů')}
            </Badge>
          )
          : <Badge variant='yellow'>bez metrics:read</Badge>}
        {!logs?.metrics.available && logs?.metrics.reason && (
          <span className='text-gray-500'>{logs.metrics.reason}</span>
        )}
      </div>

      <div className='flex gap-3 flex-wrap'>
        <Select
          value={custFilter}
          onChange={(e) => setCustFilter(e.target.value)}
        >
          <option value=''>{t('fleet.allCustomersOption')}</option>
          {(customers as Customer[]).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </Select>
        <Select
          value={chanFilter}
          onChange={(e) => setChanFilter(e.target.value)}
        >
          <option value=''>{t('fleet.allChannelsOption')}</option>
          {(labels?.channels ?? []).map((c) => <option key={c}>{c}</option>)}
        </Select>
        <Select value={computerFilter} onChange={(e) => setComputerFilter(e.target.value)}>
          <option value=''>{t('fleet.allHostsOption', 'Všechny Fleet hosty')}</option>
          {(labels?.computers ?? []).map((computer) => <option key={computer}>{computer}</option>)}
        </Select>
        <div className='flex-1 flex gap-2 min-w-48'>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('fleet.searchLogsPlaceholder')}
          />
          <Button variant='primary' onClick={doSearch} icon={<AlertCircle size={14} />}>
            {t('fleet.searchButton')}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className='p-0'>
          {isLoading
            ? <div className='p-8 text-center text-gray-600 text-sm'>{t('fleet.loadingLabel')}</div>
            : logs?.entries?.length
            ? (
              <table className='w-full text-xs font-mono'>
                <thead>
                  <tr className='border-b border-gray-800 bg-gray-900/40'>
                    <th className='text-left px-4 py-3 text-gray-500'>{t('fleet.colTime')}</th>
                    <th className='text-left px-4 py-3 text-gray-500'>Agent</th>
                    <th className='text-left px-4 py-3 text-gray-500'>{t('fleet.colChannel')}</th>
                    <th className='text-left px-4 py-3 text-gray-500'>Metrics</th>
                    <th className='text-left px-4 py-3 text-gray-500'>{t('fleet.colMessage')}</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-800/40'>
                  {logs.entries.map((e, i) => {
                    const metric = metricForAgent(logs.metrics?.nodes ?? [], e.agent);
                    return (
                      <tr key={i} className='hover:bg-gray-800/20'>
                        <td className='px-4 py-2 text-gray-500 whitespace-nowrap'>
                          {fmtDate(e.time)}
                        </td>
                        <td className='px-4 py-2 text-blue-400'>{e.agent || '—'}</td>
                        <td className='px-4 py-2 text-gray-400'>{e.channel || '—'}</td>
                        <td className='px-4 py-2 whitespace-nowrap'>
                          {metric
                            ? (
                              <span className='text-green-400'>
                                {metric.measurement} · {fmtDate(metric.ts)}
                              </span>
                            )
                            : <span className='text-gray-600'>—</span>}
                        </td>
                        <td className='px-4 py-2 text-gray-300 max-w-xl truncate'>
                          {e.message || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
            : (
              <EmptyState
                icon={<List className='w-12 h-12 text-gray-700' />}
                title={t('fleet.noRecordsTitle')}
                description={t('fleet.noLogsFoundDescription')}
              />
            )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── RDS ───────────────────────────────────────────────────────────────────────

function RdsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: agents = [], isLoading, refetch } = useQuery({
    queryKey: ['fl-rds'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; agents: RdsAgent[] }>('fleet.rds', {
        action: 'list',
      });
      return r.agents ?? [];
    },
    refetchInterval: 60_000,
  });

  const process = useMutation({
    mutationFn: (id: string) => api.mcp('fleet.rds', { action: 'process', agent_id: id }),
    onSuccess: () => {
      toast.success(t('fleet.processedToast'));
      qc.invalidateQueries({ queryKey: ['fl-rds'] });
      qc.invalidateQueries({ queryKey: ['fl-spla-rds'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <SectionHeader
          title={t('fleet.rdsShortTitle')}
          description={t('fleet.uniqueLoginsMonthDescription')}
        />
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
      </div>
      {isLoading
        ? <p className='text-sm text-gray-500 py-4'>{t('fleet.loadingLabel')}</p>
        : (agents as RdsAgent[]).length === 0
        ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<Activity className='w-12 h-12 text-gray-700' />}
                title={t('fleet.noRdsAgentsTitle')}
                description={t('fleet.dataAfterFirstProcessingDescription')}
              />
            </CardContent>
          </Card>
        )
        : (
          (agents as RdsAgent[]).map((a) => (
            <Card key={a.id}>
              <CardHeader>
                <div className='flex items-center justify-between gap-4'>
                  <div className='min-w-0'>
                    <CardTitle icon={Monitor}>{a.id}</CardTitle>
                    <p className='text-xs text-gray-500 mt-0.5'>
                      {t('fleet.monthProcessedLabel', {
                        month: a.month,
                        date: fmtDate(a.last_processed_ts),
                      })}
                    </p>
                  </div>
                  <div className='flex items-center gap-4 shrink-0'>
                    <div className='text-right'>
                      <p className='text-xl font-bold text-white'>{a.unique_count ?? 0}</p>
                      <p className='text-xs text-gray-500'>{t('fleet.uniqueUsersLabel')}</p>
                    </div>
                    <div className='flex gap-2'>
                      {(a.users?.length ?? 0) > 0 && (
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                        >
                          {expanded === a.id
                            ? t('fleet.hideButton')
                            : t('fleet.usersCountButton', { count: a.users!.length })}
                        </Button>
                      )}
                      <Button
                        size='sm'
                        variant='secondary'
                        icon={<RefreshCw size={11} />}
                        onClick={() => process.mutate(a.id)}
                        loading={process.isPending}
                      >
                        Process
                      </Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              {expanded === a.id && a.users && a.users.length > 0 && (
                <CardContent>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b border-gray-800'>
                        <th className='text-left py-2 text-xs text-gray-400'>
                          {t('fleet.colUser')}
                        </th>
                        <th className='text-left py-2 text-xs text-gray-400'>
                          {t('fleet.colFirstLogin')}
                        </th>
                        <th className='text-right py-2 text-xs text-gray-400'>
                          {t('fleet.colLogins')}
                        </th>
                        <th className='text-left py-2 text-xs text-gray-400'>
                          {t('fleet.colLastLogin')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-800/40'>
                      {a.users.map((u) => (
                        <tr key={u.user}>
                          <td className='py-2 text-white font-mono text-xs'>{u.user}</td>
                          <td className='py-2 text-gray-400 text-xs'>{fmtDate(u.first_login)}</td>
                          <td className='py-2 text-right text-gray-300'>{u.login_count}</td>
                          <td className='py-2 text-gray-400 text-xs'>{fmtDate(u.last_login)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              )}
            </Card>
          ))
        )}
    </div>
  );
}

// ── SQL ───────────────────────────────────────────────────────────────────────

function SqlTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: agents = [], isLoading, refetch } = useQuery({
    queryKey: ['fl-sql'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; agents: SqlAgent[] }>('fleet.sql', {
        action: 'list',
      });
      return r.agents ?? [];
    },
    refetchInterval: 120_000,
  });

  const setLicenseType = useMutation({
    mutationFn: ({ agentId, instance, lt }: { agentId: string; instance: string; lt: string }) =>
      api.mcp('fleet.sql', {
        action: 'set_license',
        agent_id: agentId,
        instance,
        license_type: lt,
      }),
    onSuccess: () => {
      toast.success(t('fleet.savedToast'));
      qc.invalidateQueries({ queryKey: ['fl-sql'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <SectionHeader
          title='SQL Server'
          description={t('fleet.sqlDescriptionLabel')}
        />
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
      </div>
      {isLoading
        ? <p className='text-sm text-gray-500 py-4'>{t('fleet.loadingLabel')}</p>
        : (agents as SqlAgent[]).length === 0
        ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<Database className='w-12 h-12 text-gray-700' />}
                title={t('fleet.noSqlAgentsTitle')}
                description={t('fleet.dataAfterFirstSqlEventDescription')}
              />
            </CardContent>
          </Card>
        )
        : (
          (agents as SqlAgent[]).map((a) => {
            const instances = a.instances ?? [];
            return (
              <Card key={a.agent}>
                <CardHeader>
                  <div className='flex items-center justify-between gap-4'>
                    <div className='min-w-0'>
                      <CardTitle icon={Database}>{a.agent}</CardTitle>
                      <p className='text-xs text-gray-500 mt-0.5'>
                        {t('fleet.updatedInstancesLabel', {
                          date: fmtDate(a.updated_at),
                          count: instances.length,
                        })}
                      </p>
                    </div>
                    <div className='flex gap-2 shrink-0'>
                      {instances.length > 0 && (
                        <Button
                          size='sm'
                          variant='ghost'
                          onClick={() => setExpanded(expanded === a.agent ? null : a.agent)}
                        >
                          {expanded === a.agent
                            ? t('fleet.hideDetailButton')
                            : t('fleet.userDetailButton')}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {instances.length === 0
                    ? <p className='text-xs text-gray-600 py-2'>{t('fleet.noSqlInstancesLabel')}</p>
                    : (
                      <table className='w-full text-sm'>
                        <thead>
                          <tr className='border-b border-gray-800'>
                            <th className='text-left py-2.5 text-xs text-gray-400'>Instance</th>
                            <th className='text-left py-2.5 text-xs text-gray-400'>Edition</th>
                            <th className='text-left py-2.5 text-xs text-gray-400'>
                              {t('fleet.colVersion')}
                            </th>
                            <th className='text-left py-2.5 text-xs text-gray-400'>
                              {t('fleet.colLicenseType')}
                            </th>
                            <th className='text-right py-2.5 text-xs text-gray-400'>
                              {t('fleet.colCores')}
                            </th>
                            <th className='text-right py-2.5 text-xs text-gray-400'>
                              {t('fleet.colUniqueUsers')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-gray-800/40'>
                          {instances.map((inst) => (
                            <tr key={inst.instance}>
                              <td className='py-2.5 text-white font-mono text-xs'>
                                {inst.instance}
                              </td>
                              <td className='py-2.5 text-gray-400 text-xs'>
                                {inst.edition ?? '—'}
                              </td>
                              <td className='py-2.5 text-gray-500 text-xs'>
                                {inst.version_name ?? inst.version ?? '—'}
                              </td>
                              <td className='py-2.5'>
                                {inst.license_type === 'free'
                                  ? <Badge variant='gray'>Free / Dev</Badge>
                                  : (
                                    <Select
                                      size='sm'
                                      value={inst.license_type}
                                      onChange={(e) =>
                                        setLicenseType.mutate({
                                          agentId: a.agent,
                                          instance: inst.instance,
                                          lt: e.target.value,
                                        })}
                                    >
                                      <option value='core'>Core</option>
                                      <option value='user_cal'>User CAL</option>
                                    </Select>
                                  )}
                              </td>
                              <td className='py-2.5 text-right text-xs'>
                                {inst.license_type === 'core'
                                  ? (
                                    <span className='text-white font-medium'>
                                      {((inst.cpu_count ?? 0) * (inst.cores_per_socket || 1)) ||
                                        (inst.cpu_count ?? '—')}
                                    </span>
                                  )
                                  : <span className='text-gray-600'>—</span>}
                              </td>
                              <td className='py-2.5 text-right text-xs'>
                                {inst.license_type === 'user_cal'
                                  ? (
                                    <span className='text-white font-medium'>
                                      {inst.unique_user_count ?? 0}
                                    </span>
                                  )
                                  : (
                                    <span className='text-gray-400'>
                                      {inst.unique_user_count ?? 0}
                                    </span>
                                  )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                </CardContent>
              </Card>
            );
          })
        )}
    </div>
  );
}

// ── KMS ───────────────────────────────────────────────────────────────────────

function KmsTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: agents = [], isLoading, refetch } = useQuery({
    queryKey: ['fl-kms'],
    queryFn: async () => {
      const r = await api.mcp<{ ok: boolean; agents: KmsAgent[] }>('fleet.kms', {
        action: 'list',
      });
      return r.agents ?? [];
    },
    refetchInterval: 120_000,
  });

  const setTag = useMutation({
    mutationFn: ({ agentId, cmid, tag }: { agentId: string; cmid: string; tag: string | null }) =>
      api.mcp('fleet.kms', { action: 'tag', agent_id: agentId, cmid, tag }),
    onSuccess: () => {
      toast.success(t('fleet.tagSavedToast'));
      qc.invalidateQueries({ queryKey: ['fl-kms'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const process = useMutation({
    mutationFn: (id: string) => api.mcp('fleet.kms', { action: 'process', agent_id: id }),
    onSuccess: () => {
      toast.success(t('fleet.processedToast'));
      qc.invalidateQueries({ queryKey: ['fl-kms'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const tagVariant = (tag?: string | null) => {
    if (tag === 'pre_production') return { label: 'Pre-prod', variant: 'yellow' as const };
    if (tag === 'test') return { label: 'Test', variant: 'gray' as const };
    return null;
  };

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <SectionHeader
          title={t('fleet.kmsServersTitle')}
          description={t('fleet.volumeActivationDescription')}
        />
        <RefreshButton onRefresh={() => refetch()} loading={isLoading} />
      </div>
      {isLoading
        ? <p className='text-sm text-gray-500 py-4'>{t('fleet.loadingLabel')}</p>
        : (agents as KmsAgent[]).length === 0
        ? (
          <Card>
            <CardContent>
              <EmptyState
                icon={<Lock className='w-12 h-12 text-gray-700' />}
                title={t('fleet.noKmsAgentsTitle')}
                description={t('fleet.dataAfterEventIdDescription')}
              />
            </CardContent>
          </Card>
        )
        : (
          (agents as KmsAgent[]).map((a) => {
            const clients = a.clients ?? [];
            return (
              <Card key={a.agent}>
                <CardHeader>
                  <div className='flex items-center justify-between gap-4'>
                    <div className='min-w-0'>
                      <CardTitle icon={Lock}>{a.agent}</CardTitle>
                      <p className='text-xs text-gray-500 mt-0.5'>
                        {t('fleet.monthProcessedLabel', {
                          month: a.month,
                          date: fmtDate(a.last_processed_ts),
                        })}
                      </p>
                    </div>
                    <div className='flex items-center gap-4 shrink-0'>
                      <div className='flex gap-6 text-center'>
                        <div>
                          <p className='text-xl font-bold text-white'>{a.unique_count ?? 0}</p>
                          <p className='text-xs text-gray-500'>{t('fleet.clientsLabel')}</p>
                        </div>
                        <div>
                          <p className='text-xl font-bold text-green-400'>
                            {a.billable_count ?? a.unique_count ?? 0}
                          </p>
                          <p className='text-xs text-gray-500'>billable</p>
                        </div>
                        <div>
                          <p className='text-xl font-bold text-gray-300'>{a.event_count ?? 0}</p>
                          <p className='text-xs text-gray-500'>{t('fleet.activationsLabel')}</p>
                        </div>
                      </div>
                      <div className='flex gap-2'>
                        {clients.length > 0 && (
                          <Button
                            size='sm'
                            variant='ghost'
                            onClick={() => setExpanded(expanded === a.agent ? null : a.agent)}
                          >
                            {expanded === a.agent
                              ? t('fleet.hideButton')
                              : t('fleet.clientsButton')}
                          </Button>
                        )}
                        <Button
                          size='sm'
                          variant='secondary'
                          icon={<RefreshCw size={11} />}
                          onClick={() => process.mutate(a.agent)}
                          loading={process.isPending}
                        >
                          Process
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                {expanded === a.agent && clients.length > 0 && (
                  <CardContent>
                    <table className='w-full text-sm'>
                      <thead>
                        <tr className='border-b border-gray-800'>
                          <th className='text-left py-2 text-xs text-gray-400'>
                            {t('fleet.colMachine')}
                          </th>
                          <th className='text-left py-2 text-xs text-gray-400'>
                            {t('fleet.colProduct')}
                          </th>
                          <th className='text-left py-2 text-xs text-gray-400'>IP</th>
                          <th className='text-left py-2 text-xs text-gray-400'>
                            {t('fleet.colLastLogin')}
                          </th>
                          <th className='text-right py-2 text-xs text-gray-400'>
                            {t('fleet.colCount')}
                          </th>
                          <th className='text-left py-2 text-xs text-gray-400'>Tag</th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-800/40'>
                        {clients.map((c) => {
                          const tg = tagVariant(c.tag);
                          return (
                            <tr key={c.cmid} className={c.tag ? 'opacity-60' : ''}>
                              <td className='py-2 text-white text-xs font-mono'>
                                {c.machine_name || '—'}
                              </td>
                              <td className='py-2 text-gray-400 text-xs truncate max-w-[200px]'>
                                {c.product || '—'}
                              </td>
                              <td className='py-2 text-gray-500 text-xs font-mono'>
                                {c.ip || '—'}
                              </td>
                              <td className='py-2 text-gray-500 text-xs'>
                                {fmtDate(c.last_contact)}
                              </td>
                              <td className='py-2 text-right text-gray-400 text-xs'>
                                {c.contact_count}
                              </td>
                              <td className='py-2'>
                                {tg
                                  ? (
                                    <button
                                      onClick={() =>
                                        setTag.mutate({
                                          agentId: a.agent,
                                          cmid: c.cmid,
                                          tag: null,
                                        })}
                                      className='cursor-pointer hover:opacity-70'
                                      title={t('fleet.clickRemoveTagTooltip')}
                                    >
                                      <Badge variant={tg.variant}>{tg.label} ×</Badge>
                                    </button>
                                  )
                                  : (
                                    <Select
                                      size='sm'
                                      defaultValue=''
                                      onChange={(e) => {
                                        if (e.target.value) {
                                          setTag.mutate({
                                            agentId: a.agent,
                                            cmid: c.cmid,
                                            tag: e.target.value,
                                          });
                                        }
                                      }}
                                    >
                                      <option value=''>{t('fleet.selectTagOption')}</option>
                                      <option value='pre_production'>Pre-prod</option>
                                      <option value='test'>Test</option>
                                    </Select>
                                  )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                )}
              </Card>
            );
          })
        )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Fleet() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('fleet.pageTitle')}
        description={t('fleet.pageDescription')}
        icon={Server}
      />
      <Tabs defaultTab='alloy-agents'>
        <TabList>
          <Tab id='dashboard' icon={<LayoutDashboard size={14} />}>{t('fleet.tabOverview')}</Tab>
          <Tab id='go-agents' icon={<Shield size={14} />}>{t('fleet.tabFleetAgents')}</Tab>
          <Tab id='groups' icon={<Layers size={14} />}>{t('fleet.tabGroups')}</Tab>
          <Tab id='catalog' icon={<Boxes size={14} />}>{t('fleet.tabCatalog')}</Tab>
          <Tab id='alloy-agents' icon={<Monitor size={14} />}>{t('fleet.tabAlloyAgents')}</Tab>
          <Tab id='enroll' icon={<Plus size={14} />}>{t('fleet.tabDeploy')}</Tab>
          <Tab id='spla' icon={<BarChart2 size={14} />}>SPLA</Tab>
          <Tab id='logs' icon={<List size={14} />}>{t('fleet.tabLogs')}</Tab>
          <Tab id='rds' icon={<Activity size={14} />}>RDS</Tab>
          <Tab id='sql' icon={<Database size={14} />}>SQL</Tab>
          <Tab id='kms' icon={<Lock size={14} />}>KMS</Tab>
        </TabList>
        <div className='mt-6'>
          <TabPanel id='dashboard'>
            <DashboardTab />
          </TabPanel>
          <TabPanel id='go-agents'>
            <GoAgentsTab />
          </TabPanel>
          <TabPanel id='groups'>
            <GroupsTab />
          </TabPanel>
          <TabPanel id='catalog'>
            <CatalogTab />
          </TabPanel>
          <TabPanel id='alloy-agents'>
            <AlloyAgentsTab />
          </TabPanel>
          <TabPanel id='enroll'>
            <EnrollTab />
          </TabPanel>
          <TabPanel id='spla'>
            <SplaTab />
          </TabPanel>
          <TabPanel id='logs'>
            <LogsTab />
          </TabPanel>
          <TabPanel id='rds'>
            <RdsTab />
          </TabPanel>
          <TabPanel id='sql'>
            <SqlTab />
          </TabPanel>
          <TabPanel id='kms'>
            <KmsTab />
          </TabPanel>
        </div>
      </Tabs>
    </div>
  );
}
